import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useReducer,
  useEffect,
} from "react";

import { TableContextActions } from "@hooks/use-table-context/types";
import {
  INITIAL_CONTEXT_STATE,
  INITIAL_CONTEXT_DISPATCH,
  ID_KEY,
  HIDDEN_COLUMNS_KEY,
  buildFetchParams,
  BaseTableStateHandler,
} from "@hooks/use-table-context/utils";
import {
  useCommonTableActions,
  useTableDataFetcher,
} from "@hooks/use-table-context";
import {
  AssetTableContextValue,
  AssetTableDispatchValue,
  AssetTableFilter,
  AssetTableData,
  AssetTableContextDispatches,
} from "./types";
import { ASSET_FILTER_TABLE_KEY, ASSET_FILTER_SCHEMA_VERSION } from "./utils";
import {
  loadPersistedTableFilterState,
  savePersistedTableFilterState,
} from "@hooks/use-table-context/persistence/table-filter-storage";
import { useGetAssets } from "@hooks/use-get/assets";
import { useUpdateAsset } from "@hooks/use-update/asset";
import { useDeleteAsset } from "@hooks/use-delete/asset";
import { useUserContext } from "@hooks/use-auth-context";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { getLocalStorageItem } from "@utils/index";
import { useScreenAlert } from "@hooks/use-screen-alert";
import { useAssetsConfigContext } from "@hooks/use-config-context/asset";
import { ASSET_GOOGLE_SYNC_COLUMN_IDS } from "@hooks/use-config/asset/utils";

const AssetTableContext = createContext<AssetTableContextValue>(
  INITIAL_CONTEXT_STATE,
);
const AssetTableDispatchContext = createContext<AssetTableDispatchValue>(
  INITIAL_CONTEXT_DISPATCH,
);

const assetTableContextReducer = (
  state: AssetTableContextValue,
  action: { type: TableContextActions; payload: any },
) => {
  const stateHandler = new BaseTableStateHandler<AssetTableData, AssetTableFilter>(state);
  stateHandler[action.type](action.payload as never);
  const updatedState = stateHandler.getState();
  return updatedState; // Return the updated state after applying the action
};

export interface AssetTableContextProviderProps extends PropsWithChildren {
  /**
   * Distinguishes multiple simultaneous mounts of the Asset table (e.g. the
   * standalone Assets page vs. one or more dashboard widgets) so persisted
   * filters don't overwrite each other. Omit only when a single instance can
   * ever exist at a time.
   */
  instanceId?: string;
}

export const AssetTableContextProvider = ({ children, instanceId }: AssetTableContextProviderProps) => {
  const { config: assetsConfig } = useAssetsConfigContext();
  const { siteId } = useMultiSiteContext();

  // Read once at mount — same timing as the legacy hiddenColumns-only read
  // below, and before UserContext has hydrated (see table-filter-storage.ts).
  const persistedFilterState = useMemo(
    () =>
      loadPersistedTableFilterState<AssetTableFilter>(
        ASSET_FILTER_TABLE_KEY,
        siteId,
        ASSET_FILTER_SCHEMA_VERSION,
        instanceId,
      ),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const repeaterColumnIds = useMemo(() => {
    return Object.values(assetsConfig ?? {})
      .filter((field) => {
        const clone: boolean = field.clone ?? false;
        const id: string = field.id;
        return (
          clone &&
          clone === true &&
          id !== "asset_to_user_to" &&
          id !== "ticket_to_asset_from"
        );
      })
      .map((field) => field.id);
  }, [assetsConfig]);

  // Memoized to avoid recalculation on every render. Falls back to the old
  // global hiddenColumns key so existing users' column preferences migrate
  // forward the first time they load with no scoped filter state saved yet.
  const initialHiddenColumns = useMemo(() => {
    if (persistedFilterState) return persistedFilterState.hiddenColumns;
    const storedHiddenColumns = getLocalStorageItem(HIDDEN_COLUMNS_KEY);
    return storedHiddenColumns ? JSON.parse(storedHiddenColumns) : [ID_KEY];
  }, [persistedFilterState]);
  const [state, dispatch] = useReducer<
    React.Reducer<AssetTableContextValue, AssetTableContextDispatches>
  >(assetTableContextReducer, {
    ...INITIAL_CONTEXT_STATE,
    hiddenColumns: initialHiddenColumns,
    filters: persistedFilterState?.filters ?? INITIAL_CONTEXT_STATE.filters,
    perPage: persistedFilterState?.perPage ?? INITIAL_CONTEXT_STATE.perPage,
  });

  const { loading, getAssets } = useGetAssets();
  const { loading: deleteAssetLoading, deleteAsset } = useDeleteAsset();
  const { updateAsset, loading: updateAssetLoading } = useUpdateAsset();
  const { setError } = useScreenAlert();
  const { checkUserAuthentication } = useUserContext();

  useEffect(() => {
    const current = state.hiddenColumns ?? [];
    const next = Array.from(
      new Set([...current, ...repeaterColumnIds, ID_KEY, ...ASSET_GOOGLE_SYNC_COLUMN_IDS]),
    );
    const hasNewIds = next.some((id) => !current.includes(id));
    if (hasNewIds) {
      dispatch({
        type: TableContextActions.SET_HIDDEN_COLUMNS,
        payload: next,
      });
    }
    // Intentionally re-runs only on repeaterColumnIds — state.hiddenColumns is
    // read from the latest closure each run, and hasNewIds guards re-dispatch.
  }, [repeaterColumnIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- actions
  // Get all common table actions from the shared hook
  const commonActions = useCommonTableActions<AssetTableData, AssetTableFilter>(
    dispatch,
    getAssets,
    deleteAsset,
    updateAsset,
    state,
  );

  // Destructure common actions for easy access
  const {
    setItems,
    addFilter,
    refreshItems,
    deleteItems,
    editItems,
    removeFilter,
    removeAllFilters,
    setSearchValue,
    clearSearchValue,
    selectItem,
    setPage,
    setPerPage,
    addHiddenColumn,
    removeHiddenColumn,
  } = commonActions;

  // Context-specific actions below

  // Memoize the combined loading state to prevent unnecessary re-renders
  const isLoading = useMemo(
    () => state.loading || loading || updateAssetLoading || deleteAssetLoading,
    [state.loading, loading, updateAssetLoading, deleteAssetLoading],
  );

  // fetch assets on initial load or when data inputs change
  useTableDataFetcher({
    state,
    loading,
    otherLoadingStates: [deleteAssetLoading, updateAssetLoading],
    fetchData: getAssets,
    getFetchParams: buildFetchParams<AssetTableFilter, AssetTableData>,
    setItems,
    checkUserAuthentication,
    setError,
  });

  // Persist filters + hidden columns + page size (per user/subsite) whenever
  // any of them change — survives refresh and login.
  useEffect(() => {
    const hiddenColumnsUpdate = state.hiddenColumns.includes(ID_KEY)
      ? state.hiddenColumns
      : [...state.hiddenColumns, ID_KEY];
    savePersistedTableFilterState<AssetTableFilter>(
      ASSET_FILTER_TABLE_KEY,
      siteId,
      ASSET_FILTER_SCHEMA_VERSION,
      {
        filters: state.filters,
        hiddenColumns: hiddenColumnsUpdate,
        perPage: state.perPage,
      },
      instanceId,
    );
  }, [state.filters, state.hiddenColumns, state.perPage, siteId, instanceId]);

  // dispatch provider value
  const dispatchValue = useMemo(
    () => ({
      setItems,
      addFilter,
      removeFilter,
      removeAllFilters,
      setSearchValue,
      clearSearchValue,
      editItems,
      selectItem,
      refreshItems,
      deleteItems,
      setPage,
      setPerPage,
      addHiddenColumn,
      removeHiddenColumn,
    }),
    [
      setItems,
      addFilter,
      removeFilter,
      removeAllFilters,
      setSearchValue,
      clearSearchValue,
      editItems,
      selectItem,
      refreshItems,
      deleteItems,
      setPage,
      setPerPage,
      addHiddenColumn,
      removeHiddenColumn,
    ],
  );

  return (
    <AssetTableDispatchContext.Provider value={dispatchValue}>
      <AssetTableContext.Provider
        value={{
          ...state,
          loading: isLoading,
        }}
      >
        {children}
      </AssetTableContext.Provider>
    </AssetTableDispatchContext.Provider>
  );
};

export const useAssetTableContext = () => {
  const context = useContext(AssetTableContext);
  if (!context) {
    throw new Error(
      "useAssetTableContext must be used within an AssetTableContextProvider",
    );
  }
  return context;
};

export const useAssetTableDispatch = () => {
  const dispatchContext = useContext(AssetTableDispatchContext);
  if (!dispatchContext) {
    throw new Error(
      "useAssetTableDispatch must be used within an AssetTableContextProvider",
    );
  }
  return dispatchContext;
};
